import { getMaskThumbnailUploadS3SignedUrl } from '@/api/image';
import { registerMask } from '@/api/masks';
import type { MaskDto } from '@/api/typings/mask';
import { imageToBlob, uploadImageToS3 } from './imageUtils';
import { uploadFileToS3 } from './s3Utils';
import { canvasToBlob, createCanvas } from './canvasUtils';
import { parseSvgMaskShape, sanitizeSvgString } from './fabricUtils';

export const MASK_UPLOAD_ACCEPT = '.png,.jpg,.jpeg,.svg';
export const RASTER_MASK_UPLOAD_ACCEPT = '.png,.jpg,.jpeg';

const THUMBNAIL_SIZE = 50;

type PreparedMask = { maskFile: File; thumbnail: Blob };

/**
 * Uploads a mask file and registers it on the user's mask list so it can be reused on other
 * templates. The thumbnail is what the mask picker renders; the uploaded file itself is only used
 * to apply the mask on the placeholder. Both files are rendered here and pushed straight to S3 with
 * signed urls, so neither one is processed server side.
 */
export const uploadAndRegisterMask = async (file: File): Promise<MaskDto> => {
    const { maskFile, thumbnail } = isSvgMask(file) ? await prepareSvgMask(file) : await prepareRasterMask(file);
    const uploaded = await uploadImageToS3('mask', maskFile);
    const thumbnailPath = await uploadMaskThumbnail(uploaded.Name, thumbnail);

    return registerMask({
        path: uploaded.Path,
        thumbnailPath: thumbnailPath,
        name: file.name.replace(/\.[^.]+$/, '')
    });
};

export const isSvgMask = (fileOrPath: File | string): boolean => {
    const name = typeof fileOrPath === 'string' ? fileOrPath : fileOrPath.name;
    return name.toLowerCase().endsWith('.svg');
};

/**
 * Thumbnails live in a `thumbnails` folder next to the mask, always as png. Deriving the path
 * avoids persisting it on the element or asking the server for it every time a template is loaded.
 */
export const deriveMaskThumbnailPath = (maskPath: string): string => {
    const separator = maskPath.lastIndexOf('/');
    const folder = maskPath.slice(0, separator);
    const fileName = maskPath.slice(separator + 1).replace(/\.[^.]+$/, '');

    return `${folder}/thumbnails/${fileName}.png`;
};

export const maskNameFromPath = (maskPath: string): string =>
    maskPath.slice(maskPath.lastIndexOf('/') + 1).replace(/\.[^.]+$/, '');

/**
 * The stored file is served back from our own CloudFront distribution as image/svg+xml, so it is
 * sanitized before upload and not only when it is read back into the canvas. The thumbnail is
 * rasterized from the same shape that ends up being applied as a clip path, so a document that
 * cannot be parsed is rejected here instead of when the mask is picked.
 */
const prepareSvgMask = async (file: File): Promise<PreparedMask> => {
    const svgString = sanitizeSvgString(await file.text());
    const shape = await parseSvgMaskShape(svgString);
    const multiplier = Math.min(THUMBNAIL_SIZE / shape.width!, THUMBNAIL_SIZE / shape.height!);
    const thumbnail = await imageToBlob(shape.toDataURL({ format: 'png', multiplier: multiplier }));

    if (!thumbnail) {
        throw new Error(`'${file.name}' could not be rendered as a thumbnail`);
    }

    return {
        maskFile: new File([svgString], file.name, { type: 'image/svg+xml' }),
        thumbnail: thumbnail
    };
};

/**
 * Scaled down copy of the thresholded mask, so the picker shows the same black and white shape that
 * gets applied. Masks smaller than the thumbnail are left as they are instead of being upscaled.
 */
const toThumbnail = (mask: OffscreenCanvas | HTMLCanvasElement): Promise<Blob> => {
    const scale = Math.min(THUMBNAIL_SIZE / mask.width, THUMBNAIL_SIZE / mask.height, 1);
    const canvas = createCanvas(Math.round(mask.width * scale), Math.round(mask.height * scale));
    const context = canvas.getContext('2d') as CanvasRenderingContext2D;

    context.drawImage(mask as CanvasImageSource, 0, 0, canvas.width, canvas.height);

    return canvasToBlob(canvas);
};

const uploadMaskThumbnail = async (maskFileName: string, thumbnail: Blob): Promise<string> => {
    const signedUrl = await getMaskThumbnailUploadS3SignedUrl(maskFileName);
    await uploadFileToS3(signedUrl.SignedUrl, signedUrl.Mime, thumbnail, signedUrl.CacheControl);

    return signedUrl.Path;
};

const BRIGHTNESS_THRESHOLD = 128;

/**
 * Raster masks are stored already thresholded to pure black and white, which is what the placeholder
 * expects when the mask is later reloaded from its path. SVG masks keep their shape untouched because
 * they are applied as a clip path, not as a luminance mask.
 */
const prepareRasterMask = (file: File): Promise<PreparedMask> =>
    new Promise((resolve, reject) => {
        const image = new Image();

        image.onload = async () => {
            const canvas = createCanvas(image.naturalWidth, image.naturalHeight);
            const context = canvas.getContext('2d') as CanvasRenderingContext2D | null;
            if (!context) {
                reject(new Error('Could not read the mask image'));
                return;
            }

            context.drawImage(image, 0, 0);
            const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            for (let i = 0; i < data.length; i += 4) {
                const brightness = data[i] * 0.3 + data[i + 1] * 0.59 + data[i + 2] * 0.11;
                data[i] = data[i + 1] = data[i + 2] = brightness > BRIGHTNESS_THRESHOLD ? 255 : 0;
            }
            context.putImageData(imageData, 0, 0);

            try {
                const blob = await canvasToBlob(canvas);
                resolve({
                    maskFile: new File([blob], `${file.name.replace(/\.[^.]+$/, '')}.png`, { type: 'image/png' }),
                    thumbnail: await toThumbnail(canvas)
                });
            } catch (error) {
                reject(error);
            } finally {
                URL.revokeObjectURL(image.src);
            }
        };

        image.onerror = () => {
            URL.revokeObjectURL(image.src);
            reject(new Error(`'${file.name}' could not be read as an image`));
        };

        image.src = URL.createObjectURL(file);
    });
